<template>
  <d-container fluid class="main-content-container px-4 statistics">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('stats.subtitle') }}</span>
        <h3 class="page-title">{{ $t('stats.title') }}</h3>
      </d-col>

      <d-col col sm="6" class="text-right">
        <d-button
          theme="success"
          class="ml-2 text-nowrap"
          :disabled="downloadInProgress"
          @click.prevent.stop="downloadXlsx">
          <div
            v-if="downloadInProgress"
            class="spinner-border spinner-border-sm"
            role="status"></div>
          <template v-if="!downloadInProgress">
            <i class="material-icons">cloud_download</i>
            {{ $t('stats.downloadData') }}
          </template>
        </d-button>
      </d-col>
    </d-row>

    <d-row
      style="width: 100%;"
      class="d-flex ml-0 mb-2 page-filters">
      <validation-observer
        ref="statistics-filters-form"
        tag="form"
        style="width: 100%;"
        class="d-flex">

        <d-button-group class="mr-2">
          <d-button
            :outline="filters.userOrGroup !== 'user'"
            @click.prevent="filters.userOrGroup = 'user'">
            {{ $t('users.worker') }}
          </d-button>
          <d-button
            :outline="filters.userOrGroup !== 'group'"
            @click.prevent="filters.userOrGroup = 'group'">
            {{ $t('settings.userGroups.userGroup') }}
          </d-button>
        </d-button-group>

        <v-select
          v-if="loadedWorkers && filters.userOrGroup === 'user'"
          class="mr-2 w-50"
          style="max-width: 450px"
          :options="orderWorkerOptions"
          v-model="filters.user_id"
          :multiple="true"
          :placeholder="$t('order.isWorker')"
          :searchable="false"
          label="text">
        </v-select>

        <v-select
          v-if="loadedGroups && filters.userOrGroup === 'group'"
          class="mr-2 w-25"
          :options="groupOptions"
          v-model="filters.group_id"
          :multiple="false"
          :placeholder="$t('settings.userGroups.userGroup') + '?'"
          :searchable="false"
          label="text">
        </v-select>

        <validation-provider
          rules="required"
          name="date"
          v-slot="{ errors }">
          <date-range-picker
            :locale-data="{
              'firstDay': 1,
              'format': 'dd-mm-yyyy',
              'daysOfWeek': [$t('ui.daysOfWeek.sun'), $t('ui.daysOfWeek.mon'), $t('ui.daysOfWeek.tue'), $t('ui.daysOfWeek.wed'), $t('ui.daysOfWeek.thur'), $t('ui.daysOfWeek.fri'), $t('ui.daysOfWeek.sat')],
              'monthNames': [$t('ui.monthNames.jan'), $t('ui.monthNames.feb'), $t('ui.monthNames.mar'), $t('ui.monthNames.apr'), $t('ui.monthNames.may'), $t('ui.monthNames.jun'), $t('ui.monthNames.jul'), $t('ui.monthNames.aug'), $t('ui.monthNames.sep'), $t('ui.monthNames.oct'), $t('ui.monthNames.nov'), $t('ui.monthNames.dec')],
            }"
            :singleDatePicker="false"
            :timePicker="false"
            :showWeekNumbers="false"
            :showDropdowns="false"
            :autoApply="true"
            v-model="filters.period"
            :ranges="false"
            :linkedCalendars="false"
            class="mr-2" />
          <div class="invalid-feedback">
            {{ errors[0] }}
          </div>
        </validation-provider>

        <d-checkbox
          inline
          toggle-small
          v-model="filters.compare_periods"
          toggle
          class="mt-1">
          {{ $t('stats.comparePeriods') }}
        </d-checkbox>

        <date-range-picker
            v-if="filters.compare_periods"
            :locale-data="{
              'firstDay': 1,
              'format': 'dd-mm-yyyy',
              'daysOfWeek': [$t('ui.daysOfWeek.sun'), $t('ui.daysOfWeek.mon'), $t('ui.daysOfWeek.tue'), $t('ui.daysOfWeek.wed'), $t('ui.daysOfWeek.thur'), $t('ui.daysOfWeek.fri'), $t('ui.daysOfWeek.sat')],
              'monthNames': [$t('ui.monthNames.jan'), $t('ui.monthNames.feb'), $t('ui.monthNames.mar'), $t('ui.monthNames.apr'), $t('ui.monthNames.may'), $t('ui.monthNames.jun'), $t('ui.monthNames.jul'), $t('ui.monthNames.aug'), $t('ui.monthNames.sep'), $t('ui.monthNames.oct'), $t('ui.monthNames.nov'), $t('ui.monthNames.dec')],
            }"
            :singleDatePicker="false"
            :timePicker="false"
            :showWeekNumbers="false"
            :showDropdowns="false"
            :autoApply="true"
            v-model="filters.period_to_compare"
            :ranges="false"
            :linkedCalendars="false" />
      </validation-observer>
    </d-row>

    <d-row
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <d-form-select
        v-model="filters.region_id"
        :disabled="!loadedRegions"
        :options="regionsOptions"
        class="w-25 mr-2" />

      <d-checkbox
        inline
        toggle-small
        v-model="filters.filter_customers"
        toggle
        class="mt-1">
        {{ $t('stats.compareCustomers') }}
      </d-checkbox>

      <date-range-picker
        v-if="filters.filter_customers"
        :locale-data="{
          'firstDay': 1,
          'format': 'dd-mm-yyyy',
          'daysOfWeek': [$t('ui.daysOfWeek.sun'), $t('ui.daysOfWeek.mon'), $t('ui.daysOfWeek.tue'), $t('ui.daysOfWeek.wed'), $t('ui.daysOfWeek.thur'), $t('ui.daysOfWeek.fri'), $t('ui.daysOfWeek.sat')],
          'monthNames': [$t('ui.monthNames.jan'), $t('ui.monthNames.feb'), $t('ui.monthNames.mar'), $t('ui.monthNames.apr'), $t('ui.monthNames.may'), $t('ui.monthNames.jun'), $t('ui.monthNames.jul'), $t('ui.monthNames.aug'), $t('ui.monthNames.sep'), $t('ui.monthNames.oct'), $t('ui.monthNames.nov'), $t('ui.monthNames.dec')],
        }"
        :singleDatePicker="false"
        :timePicker="false"
        :showWeekNumbers="false"
        :showDropdowns="false"
        :autoApply="true"
        v-model="filters.period_customers"
        :ranges="false"
        :linkedCalendars="false" />

      <d-button
        theme="danger"
        outline
        :class="{ 'text-nowrap': true, 'ml-auto': true }"
        @click.prevent="resetFilters">
        {{ $t('ui.resetFilters') }}
      </d-button>

      <d-button
        theme="info"
        outline
        :class="{ 'text-nowrap': true, 'ml-2': true }"
        @click.prevent="validateFiltersForm">
        {{ $t('ui.search') }}
      </d-button>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light fixed-thead">
          <tr>
            <th scope="col" class="border-0">{{ $t('users.worker') }}</th>
            <th scope="col" class="border-0" style="width: 110px;"><small>{{ $t('stats.offersCount') }}</small></th>
            <th scope="col" class="border-0" style="width: 110px;"><small>{{ $t('stats.offersAmount') }}</small></th>
            <th scope="col" class="border-0" style="width: 110px;"><small>{{ $t('stats.ordersCount') }}</small></th>
            <th scope="col" class="border-0" style="width: 110px;"><small>{{ $t('stats.ordersAmount') }}</small></th>
            <th scope="col" class="border-0" style="width: 110px;"><small>{{ $t('stats.offersToOrdersCount') }}</small></th>
            <th scope="col" class="border-0" style="width: 110px;"><small>{{ $t('stats.offersToOrdersAmount') }}</small></th>
            <th scope="col" class="border-0" style="width: 110px;"><small>{{ $t('stats.settlementOrdersCount') }}</small></th>
            <th scope="col" class="border-0" style="width: 110px;"><small>{{ $t('stats.settlementOrdersAmount') }}</small></th>
            <th scope="col" class="border-0" style="width: 110px;"><small>{{ $t('stats.endedOrdersCount') }}</small></th>
            <th scope="col" class="border-0" style="width: 110px;"><small>{{ $t('stats.endedOrdersAmount') }}</small></th>
          </tr>
        </thead>
        <tbody v-if="loaded">
          <template v-if="filters.compare_periods && filters.period_to_compare.startDate && filters.period_to_compare.endDate">
            <tr class="table-secondary fixed-row">
              <td class="align-middle"><strong>{{ $t('stats.sumRowTitle') }}</strong></td>
              <td
                v-for="(field, index) of statsFields"
                :key="'table-cell-stats-sum-' + index">
                <strong>
                  <span :class="{
                    'text-success': statistics.data.periods[0].stats.sum[field] > statistics.data.periods[1].stats.sum[field],
                    'text-danger': statistics.data.periods[0].stats.sum[field] < statistics.data.periods[1].stats.sum[field],
                    'text-gray': statistics.data.periods[0].stats.sum[field] === statistics.data.periods[1].stats.sum[field],
                  }">
                    {{ statistics.data.periods[0].stats.sum[field] }}
                    <i class="material-icons is-inc">arrow_upward</i>
                    <i class="material-icons is-dec">arrow_downward</i>
                    <i class="material-icons is-eq">remove</i>
                  </span>
                  <br>
                  {{ statistics.data.periods[1].stats.sum[field] }}
                </strong>
              </td>
            </tr>

            <tr
              v-for="(data, index) of statistics.data.periods[0].users"
              :key="'statistics-item-' + index">
              <td class="align-middle">{{ data.name }}</td>
              <td
                v-for="(field, subindex) of statsFields"
                :key="'table-cell-stats-user-sum-' + index + '-' + subindex">
                <span :class="{
                  'text-success': data[field] > statistics.data.periods[1].users[index][field],
                  'text-danger': data[field] < statistics.data.periods[1].users[index][field],
                  'text-gray': data[field] === statistics.data.periods[1].users[index][field],
                }">
                  {{ data[field] }}
                  <i class="material-icons is-inc">arrow_upward</i>
                  <i class="material-icons is-dec">arrow_downward</i>
                  <i class="material-icons is-eq">remove</i>
                </span>
                <br>
                {{ statistics.data.periods[1].users[index][field] }}
              </td>
            </tr>
            <tr v-if="statistics.data.length === 0">
              <td
                colspan="9"
                class="text-center">
                {{ $t('ui.noInfoToDisplay') }}&hellip;
              </td>
            </tr>

            <tr
              v-for="(type, index) of ['avg', 'min', 'max']"
              :key="'stats-sum-row-' + index"
              class="table-secondary">
              <td class="align-middle"><strong>{{ $t('stats.' + type + 'RowTitle') }}</strong></td>
              <td
                v-for="(field, subindex) of statsFields"
                :key="'table-cell-stats-user-sum-total-' + index + '-' + subindex">
                <strong>
                  <span :class="{
                    'text-success': parseFloat(Number(statistics.data.periods[0].stats[type][field]).toFixed(4)) > parseFloat(Number(statistics.data.periods[1].stats[type][field]).toFixed(4)),
                    'text-danger': parseFloat(Number(statistics.data.periods[0].stats[type][field]).toFixed(4)) < parseFloat(Number(statistics.data.periods[1].stats[type][field]).toFixed(4)),
                    'text-gray': parseFloat(Number(statistics.data.periods[0].stats[type][field]).toFixed(4)) === parseFloat(Number(statistics.data.periods[1].stats[type][field]).toFixed(4)),
                  }">
                    {{ Number(statistics.data.periods[0].stats[type][field]).toFixed(4) }}
                    <i class="material-icons is-inc">arrow_upward</i>
                    <i class="material-icons is-dec">arrow_downward</i>
                    <i class="material-icons is-eq">remove</i>
                  </span>
                  <br>
                  {{ Number(statistics.data.periods[1].stats[type][field]).toFixed(4) }}
                </strong>
              </td>
            </tr>
          </template>
          <template v-else>
            <tr class="table-secondary fixed-row">
              <td class="align-middle"><strong>{{ $t('stats.sumRowTitle') }}</strong></td>
              <td><strong>{{ statistics.stats.sum.offers_count }}</strong></td>
              <td><strong>{{ statistics.stats.sum.offers_amount }}</strong></td>
              <td><strong>{{ statistics.stats.sum.orders_count }}</strong></td>
              <td><strong>{{ statistics.stats.sum.orders_amount }}</strong></td>
              <td><strong>{{ statistics.stats.sum.offers_to_orders_count }}</strong></td>
              <td><strong>{{ statistics.stats.sum.offers_to_orders_amount }}</strong></td>
              <td><strong>{{ statistics.stats.sum.settlement_orders_count }}</strong></td>
              <td><strong>{{ statistics.stats.sum.settlement_orders_amount }}</strong></td>
              <td><strong>{{ statistics.stats.sum.ended_orders_count }}</strong></td>
              <td><strong>{{ statistics.stats.sum.ended_orders_amount }}</strong></td>
            </tr>

            <tr
              v-for="(data, index) of statistics.data"
              :key="'statistics-item-' + index">
              <td class="align-middle">{{ data.name }}</td>
              <td class="stats-tooltip" @click="showStatsPopup(data.id, 'offers')">{{ data.offers_count }}</td>
              <td class="stats-tooltip" @click="showStatsPopup(data.id, 'offers')">{{ data.offers_amount }}</td>
              <td class="stats-tooltip" @click="showStatsPopup(data.id, 'orders')">{{ data.orders_count }}</td>
              <td class="stats-tooltip" @click="showStatsPopup(data.id, 'orders')">{{ data.orders_amount }}</td>
              <td class="stats-tooltip" @click="showStatsPopup(data.id, 'offers_to_orders')">{{ data.offers_to_orders_count }}</td>
              <td class="stats-tooltip" @click="showStatsPopup(data.id, 'offers_to_orders')">{{ data.offers_to_orders_amount }}</td>
              <td class="stats-tooltip" @click="showStatsPopup(data.id, 'settlement_orders')">{{ data.settlement_orders_count }}</td>
              <td class="stats-tooltip" @click="showStatsPopup(data.id, 'settlement_orders')">{{ data.settlement_orders_amount }}</td>
              <td class="stats-tooltip" @click="showStatsPopup(data.id, 'ended_orders')">{{ data.ended_orders_count }}</td>
              <td class="stats-tooltip" @click="showStatsPopup(data.id, 'ended_orders')">{{ data.ended_orders_amount }}</td>
            </tr>
            <tr v-if="statistics.data.length === 0">
              <td
                colspan="9"
                class="text-center">
                {{ $t('ui.noInfoToDisplay') }}&hellip;
              </td>
            </tr>

            <tr
              v-for="(type, index) of ['avg', 'min', 'max']"
              :key="'stats-sum-row-' + index"
              class="table-secondary">
              <td class="align-middle"><strong>{{ $t('stats.' + type + 'RowTitle') }}</strong></td>
              <td><strong>{{ Number(statistics.stats[type].offers_count).toFixed(4) }}</strong></td>
              <td><strong>{{ statistics.stats[type].offers_amount }}</strong></td>
              <td><strong>{{ Number(statistics.stats[type].orders_count).toFixed(4) }}</strong></td>
              <td><strong>{{ statistics.stats[type].orders_amount }}</strong></td>
              <td><strong>{{ Number(statistics.stats[type].offers_to_orders_count).toFixed(4) }}</strong></td>
              <td><strong>{{ statistics.stats[type].offers_to_orders_amount }}</strong></td>
              <td><strong>{{ Number(statistics.stats[type].settlement_orders_count).toFixed(4) }}</strong></td>
              <td><strong>{{ statistics.stats[type].settlement_orders_amount }}</strong></td>
              <td><strong>{{ Number(statistics.stats[type].ended_orders_count).toFixed(4) }}</strong></td>
              <td><strong>{{ statistics.stats[type].ended_orders_amount }}</strong></td>
            </tr>
          </template>
        </tbody>
      </table>

      <div v-if="!loaded" class="pb-3 pt-3 text-center">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <d-modal
      v-if="statsPopupDisplay"
      size="lg"
      @close="closeStatsPopup">
      <d-modal-header>
        <d-modal-title>

        </d-modal-title>
      </d-modal-header>
      <d-modal-body class="p-0">
        <d-alert
          v-if="!statsInPopupLoaded && !statsInPopupError"
          show
          theme="light"
          class="text-center">
          {{ $t('ui.dataLoading') }}
        </d-alert>
        <d-alert
          v-if="statsInPopupError"
          show
          theme="danger"
          class="text-center">
          {{ $t('ui.dataLoadingFailed') }}
        </d-alert>
        <d-alert
          v-if="statsInPopupLoaded && !statsInPopup.data.length"
          show
          theme="warning"
          class="text-center">
          {{ $t('purchase.noOrdersToDisplay') }}
        </d-alert>
        <table
          v-if="statsInPopupLoaded && statsInPopup.data.length"
          class="table">
          <thead>
            <tr>
              <th>{{ $t('ui.orderName') }}</th>
              <th>{{ $t('status.status') }}</th>
              <th>{{ $t('dashboard.customersPopup.customerName') }}</th>
              <th>{{ $t('stats.sumRowTitle') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(order, index) of statsInPopup.data"
              :key="'stats-in-popup-' + index">
              <td>
                <small>{{ order.order_name }}</small><br>
                <template v-if="order.settlements && order.settlements.length">
                  <small v-if="order.settlements.length === 1">
                    {{ $t('statistics.invoice') }}:
                    <span class="text-primary">
                      {{ order.settlements[0].number }}
                    </span>
                  </small>
                  <small v-if="order.settlements.length > 1">
                    {{ $t('statistics.invoices') }}:
                    <span class="text-primary">
                      {{ order.settlements.map(settlement => settlement.number).join(', ') }}
                    </span>
                  </small>
                </template>
                <template v-else>
                  <small class="text-danger">
                    {{ $t('statistics.noInvoices') }}
                  </small>
                </template>
              </td>
              <td><status-badge :status="order.status" /></td>
              <td>
                <small>
                  {{ order.customer_name }}
                </small>

                <template v-if="order.customer_region_name">
                  <br>
                  <small>
                    {{ $t('settings.regions.region') }}:
                    <span class="text-primary">
                      {{ order.customer_region_name }}
                    </span>
                  </small>
                </template>
              </td>
              <td><small>{{ order.stats_price }}</small></td>
            </tr>
          </tbody>
        </table>
      </d-modal-body>
    </d-modal>
  </d-container>
</template>

<script>
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';
import debounce from 'lodash.debounce';
import FormUtils from './../utils/FormUtils.js';
import HasFilters from './../mixins/HasFilters.vue';
import StatusBadge from './../components/common/StatusBadge.vue';

export default {
  name: 'statistics',
  mixins: [
    HasFilters
  ],
  components: {
    'date-range-picker': DateRangePicker,
    'status-badge': StatusBadge
  },
  computed: {
    disableFiltersInSubview () {
      return true;
    },
    orderWorkerOptions () {
      return this.workers.map(worker => ({
        text: worker.name,
        value: worker.id
      }));
    },
    groupOptions () {
      return this.groups.map(group => ({
        text: group.name,
        value: group.id
      }));
    },
    regionsOptions () {
      return [{ value: null, text: this.$t('settings.regions.allRegions') }].concat(this.regions.map(region => ({
        value: region.id,
        text: region.name
      })));
    }
  },
  watch: {
    'filters.period': {
      handler () {
        this.debouncedLoadFilteredData();
        this.loaded = false;
      },
      deep: true
    },
    'filters.compare_periods': {
      handler (newValue) {
        if (newValue === false) {
          this.filters.period_to_compare.startDate = null;
          this.filters.period_to_compare.endDate = null;
          this.debouncedLoadFilteredData();
          this.loaded = false;
        }
      },
      deep: true
    },
    'filters.period_to_compare': {
      handler () {
        this.debouncedLoadFilteredData();
        this.loaded = false;
      },
      deep: true
    }
  },
  data() {
    return {
      statsFields: [
        'offers_count',
        'offers_amount',
        'orders_count',
        'orders_amount',
        'offers_to_orders_count',
        'offers_to_orders_amount',
        'settlement_orders_count',
        'settlement_orders_amount',
        'ended_orders_count',
        'ended_orders_amount'
      ],
      statistics: [],
      loaded: false,
      loadError: false,
      loadedWorkers: false,
      loadedGroups: false,
      loadedRegions: false,
      filters: {
        period: {
          startDate: null,
          endDate: null
        },
        compare_periods: false,
        filter_customers: false,
        period_customers: {
          startDate: null,
          endDate: null
        },
        period_to_compare: {
          startDate: null,
          endDate: null
        },
        userOrGroup: 'user',
        region_id: null,
        user_id: null,
        group_id: null
      },
      downloadInProgress: false,
      workers: [],
      groups: [],
      regions: [],
      statsPopupDisplay: false,
      statsInPopup: [],
      statsInPopupLoaded: false,
      statsInPopupError: false
    };
  },
  mounted () {
    let currentDate = new Date();
    let firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);

    this.filters.period = {
      startDate: firstDayOfMonth,
      endDate: currentDate
    };

    this.filters.period_to_compare = {
      startDate: null,
      endDate: null
    };

    this.filters.period_customers = {
      startDate: null,
      endDate: null
    };

    this.loadAdditionalData();
    this.debouncedLoadFilteredData();
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.loaded = false;
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let whereData = {};
      let endpointUrl = '/stats/items';
      let paramsData = {};

      if (this.filters.compare_periods && this.filters.period_to_compare.startDate && this.filters.period_to_compare.endDate) {
        if (this.filters.period.startDate !== null && this.filters.period.endDate !== null) {
          endpointUrl = '/stats/compare';
          whereData.periods = [{
            date_from: null,
            date_to: null
          }, {
            date_from: null,
            date_to: null
          }];
          whereData.periods[0].date_from = [this.filters.period.startDate.getFullYear(), this.filters.period.startDate.getMonth() + 1, this.filters.period.startDate.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
          whereData.periods[0].date_to = [this.filters.period.endDate.getFullYear(), this.filters.period.endDate.getMonth() + 1, this.filters.period.endDate.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
          whereData.periods[1].date_from = [this.filters.period_to_compare.startDate.getFullYear(), this.filters.period_to_compare.startDate.getMonth() + 1, this.filters.period_to_compare.startDate.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
          whereData.periods[1].date_to = [this.filters.period_to_compare.endDate.getFullYear(), this.filters.period_to_compare.endDate.getMonth() + 1, this.filters.period_to_compare.endDate.getDate()].map(n => n < 10 ? '0' + n : n).join('-');

          if (this.filters.user_id !== null && this.filters.user_id.length) {
            whereData.periods[0].user_id = [];
            whereData.periods[1].user_id = [];

            for (let i = 0; i < this.filters.user_id.length; i++) {
              whereData.periods[0].user_id.push(this.filters.user_id[i].value);
              whereData.periods[1].user_id.push(this.filters.user_id[i].value);
            }
          }
        }

        paramsData = {
          periods: whereData.periods
        };
      } else {
        if (this.filters.period.startDate !== null && this.filters.period.endDate !== null) {
          whereData.date_from = [this.filters.period.startDate.getFullYear(), this.filters.period.startDate.getMonth() + 1, this.filters.period.startDate.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
          whereData.date_to = [this.filters.period.endDate.getFullYear(), this.filters.period.endDate.getMonth() + 1, this.filters.period.endDate.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
        }

        if (this.filters.userOrGroup === 'user' && this.filters.user_id !== null && this.filters.user_id.length) {
          whereData.user_id = [];

          for (let i = 0; i < this.filters.user_id.length; i++) {
            whereData.user_id.push(this.filters.user_id[i].value);
          }
        }

        if (this.filters.userOrGroup === 'group' && this.filters.group_id !== null && this.filters.group_id.value) {
          whereData.group_id = this.filters.group_id.value;
        }

        paramsData = {
          where: whereData
        };
      }

      if (this.filters.filter_customers && (this.filters.period_customers.startDate || this.filters.period_customers.endDate)) {
        paramsData.where.filter_customers = true;

        if (this.filters.period_customers.startDate) {
          whereData.customer_from = [
            this.filters.period_customers.startDate.getFullYear(),
            this.filters.period_customers.startDate.getMonth() + 1,
            this.filters.period_customers.startDate.getDate()
          ].map(n => n < 10 ? '0' + n : n).join('-');
        }

        if (this.filters.period_customers.endDate) {
          whereData.customer_to = [
            this.filters.period_customers.endDate.getFullYear(),
            this.filters.period_customers.endDate.getMonth() + 1,
            this.filters.period_customers.endDate.getDate()
          ].map(n => n < 10 ? '0' + n : n).join('-');
        }
      }

      if (this.filters.region_id) {
        whereData.customer_region_id = this.filters.region_id;
      }

      FormUtils.loadAdditionalData(this, {
        endpoint: endpointUrl,
        outputKey: 'statistics',
        errorKey: 'loadError',
        loadedKey: 'loaded',
        noPagination: true,
        params: paramsData
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/customers/regions/items',
        loadedKey: 'loadedRegions',
        outputKey: 'regions',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/users/items',
        loadedKey: 'loadedWorkers',
        params: {
          return: 'get',
          where: {
            type: ['worker', 'admin']
          }
        },
        outputKey: 'workers',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/users/groups/items',
        loadedKey: 'loadedGroups',
        outputKey: 'groups',
        errorKey: 'loadError',
        params: {
          return: 'get'
        }
      });
    },
    resetFilters () {
      this.filters.compare_periods = false;
      this.filters.filter_customers = false;
      this.filters.period_customers = {
        startDate: null,
        endDate: null
      };
      this.filters.period_to_compare = {
        startDate: null,
        endDate: null
      };
      this.filters.userOrGroup = 'user';
      this.filters.region_id = null;
      this.filters.user_id = null;
      this.filters.group_id = null;
    },
    showStatsPopup (id, field) {
      this.statsPopupDisplay = true;
      this.statsInPopupLoaded = false;
      this.statsInPopupError = false;
      this.statsInPopup = [];
      let dateFrom = [this.filters.period.startDate.getFullYear(), this.filters.period.startDate.getMonth() + 1, this.filters.period.startDate.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
      let dateTo = [this.filters.period.endDate.getFullYear(), this.filters.period.endDate.getMonth() + 1, this.filters.period.endDate.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
      this.loadStatsInPopup(id, field, dateFrom, dateTo, this.filters.region_id);
    },
    closeStatsPopup () {
      this.statsPopupDisplay = false;
      this.statsInPopupLoaded = false;
      this.statsInPopupError = false;
      this.statsInPopup = [];
    },
    loadStatsInPopup (id, field, dateFrom, dateTo, regionID) {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/stats/orders',
        outputKey: 'statsInPopup',
        errorKey: 'statsInPopupError',
        loadedKey: 'statsInPopupLoaded',
        noPagination: true,
        params: {
          where: {
            user_id: id,
            field,
            date_from: dateFrom,
            date_to: dateTo,
            customer_region_id: regionID
          }
        }
      });
    },
    validateFiltersForm () {
      FormUtils.validate(this.$refs['statistics-filters-form'], this.debouncedLoadFilteredData);
    },
    downloadXlsx () {
      if (this.downloadInProgress) {
        return;
      }

      let whereData = {};
      let paramsData = {};

      if (this.filters.period.startDate !== null && this.filters.period.endDate !== null) {
        whereData.date_from = [this.filters.period.startDate.getFullYear(), this.filters.period.startDate.getMonth() + 1, this.filters.period.startDate.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
        whereData.date_to = [this.filters.period.endDate.getFullYear(), this.filters.period.endDate.getMonth() + 1, this.filters.period.endDate.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
      }

      if (this.filters.userOrGroup === 'user' && this.filters.user_id !== null && this.filters.user_id.length) {
        whereData.user_id = [];

        for (let i = 0; i < this.filters.user_id.length; i++) {
          whereData.user_id.push(this.filters.user_id[i].value);
        }
      }

      if (this.filters.userOrGroup === 'group' && this.filters.group_id !== null && this.filters.group_id.value) {
        whereData.group_id = this.filters.group_id.value;
      }

      if (this.filters.region_id) {
        whereData.customer_region_id = this.filters.region_id;
      }

      paramsData = {
        where: whereData
      };

      this.downloadInProgress = true;

      this.$http.post('/stats/export', paramsData, {
        responseType: 'blob'
      }).then((response) => {
        let link = document.createElement('a');
        link.href = URL.createObjectURL(new Blob([response.data]));
        link.setAttribute('download', this.$t('stats.statsFilename') + '.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadInProgress = false;
      }).catch(() => {
        this.downloadInProgress = false;
      });
    }
  }
};
</script>

<style lang="scss">
.statistics .page-filters .vdp-datepicker .form-control[readonly] {
  background: #fff!important;
}

.statistics .fixed-thead,
.statistics .fixed-row {
  box-shadow: 0 5px 5px rgba(0, 0, 0, .1);
  position: sticky;
  top: 0;
}

.statistics .vue-daterange-picker {
  width: 250px;
}

.statistics .fixed-row {
  top: 83px;
}

.statistics .stats-tooltip {
  &:hover {
    background: #f0f0f0;
    cursor: pointer;
  }
}

.text-success {
  .material-icons.is-dec,
  .material-icons.is-eq {
    display: none;
  }
}

.text-danger {
  .material-icons.is-inc,
  .material-icons.is-eq {
    display: none;
  }
}

.text-gray {
  .material-icons.is-inc,
  .material-icons.is-dec {
    display: none;
  }
}
</style>
